import { Controller } from "@hotwired/stimulus"

// 設問チェックリスト + 選択中設問プレビュー ( 並べ替え可能 ) の汎用 controller。
//
// 左カラム: 選択可能な全設問のチェックボックスリスト ( 検索フィルタ / 全選択 / 全解除 )。
//           チェックボックスは name 無しで form 送信されない。
// 右カラム: チェックされた設問だけを表示順 ( drag&drop で並べ替え可能 ) で並べる。
//           各行に hidden input name="selected_keys[]" を持ち、 form 送信時に
//           右カラムの DOM 順がそのまま selected_keys[] の順として送られる。
//
// values:
//   - sortable: Boolean ( default: true ) - false なら drag handle / drag イベントを無効化
//   - initial-keys: String ( JSON 配列 ) - 右カラムの初期表示順 ( 通常は pdf_selected_keys 順 )
//
// 期待する DOM 構造:
//   <div data-controller="question-select"
//        data-question-select-initial-keys-value='["F1","F2"]'>
//     <input data-question-select-target="search" data-action="input->question-select#filter">
//     <div>
//       <label data-question-select-target="item"
//              data-question-key="F1"
//              data-question-label="..."
//              data-has-choices="true"
//              data-search-text="...">
//         <input type="checkbox" data-question-select-target="checkbox"
//                data-action="change->question-select#renderPreview">
//         ...
//       </label>
//     </div>
//     <div data-question-select-target="preview"></div>
//   </div>
export default class extends Controller {
  static targets = ["search", "item", "checkbox", "preview"]
  static values = {
    sortable: { type: Boolean, default: true },
    initialKeys: { type: Array, default: [] }
  }

  connect() {
    this.draggedRow = null
    this.renderPreview()
  }

  filter(event) {
    const q = event.currentTarget.value.trim().toLowerCase()
    this.itemTargets.forEach(item => {
      const text = (item.dataset.searchText || "").toLowerCase()
      item.style.display = q === "" || text.includes(q) ? "" : "none"
    })
  }

  selectAll(event) {
    event.preventDefault()
    this.visibleCheckboxes().forEach(c => c.checked = true)
    this.renderPreview()
  }

  deselectAll(event) {
    event.preventDefault()
    this.visibleCheckboxes().forEach(c => c.checked = false)
    this.renderPreview()
  }

  visibleCheckboxes() {
    return this.itemTargets
      .filter(item => item.style.display !== "none")
      .map(item => item.querySelector("input[type='checkbox']"))
      .filter(Boolean)
  }

  // チェック変更時に呼ばれる。
  // 現在の右カラム順序を保ちつつ、 新規チェック分を末尾追加、 チェック解除分を削除する。
  renderPreview() {
    if (!this.hasPreviewTarget) return

    const itemByKey = this.buildItemIndex()
    const checkedKeys = new Set(
      Object.values(itemByKey).filter(i => i.checked).map(i => i.key)
    )

    // 初回 ( 右カラムが空 ) なら initialKeysValue 順、 それ以外は現在の DOM 順を尊重
    const existingOrder = this.currentPreviewKeys()
    const orderSource = existingOrder.length > 0 ? existingOrder : this.initialKeysValue
    const kept = orderSource.filter(k => checkedKeys.has(k))
    const added = [...checkedKeys].filter(k => !kept.includes(k))
    const finalOrder = [...kept, ...added]

    if (finalOrder.length === 0) {
      this.previewTarget.innerHTML = `<div class="text-sm text-gray-400 py-12 text-center">設問を左から選択してください</div>`
      return
    }

    const rowsHtml = finalOrder
      .map(key => this.previewRowHtml(itemByKey[key]))
      .join("")
    this.previewTarget.innerHTML = `
      <div class="text-xs text-gray-500 mb-2">選択中: ${finalOrder.length} 件 ${this.sortableValue ? "( ⋮⋮ をドラッグして並べ替え。 上から PDF に出力 )" : "( チェックした順 )"}</div>
      <div class="space-y-1">${rowsHtml}</div>
    `

    if (this.sortableValue) this.bindDragEvents()
  }

  // 右カラムの現在の表示順 ( DOM 順 ) を取得する。
  currentPreviewKeys() {
    return Array.from(this.previewTarget.querySelectorAll("[data-preview-key]"))
      .map(el => el.dataset.previewKey)
  }

  buildItemIndex() {
    const map = {}
    this.itemTargets.forEach(item => {
      const checkbox = item.querySelector("input[type='checkbox']")
      if (!checkbox) return
      const key = item.dataset.questionKey || ""
      map[key] = {
        key,
        label: item.dataset.questionLabel || "",
        badgeLabel: item.dataset.questionBadgeLabel || "",
        badgeClass: item.dataset.questionBadgeClass || "",
        checked: checkbox.checked
      }
    })
    return map
  }

  previewRowHtml(item) {
    if (!item) return ""
    const handle = this.sortableValue
      ? `<span class="cursor-grab text-gray-400 shrink-0 select-none font-bold preview-handle" title="ドラッグして並べ替え">⋮⋮</span>`
      : ""
    const typeBadge = item.badgeLabel
      ? `<span class="shrink-0 inline-flex items-center rounded px-1 py-0.5 text-[9px] font-medium ${this.escapeHtml(item.badgeClass)}">${this.escapeHtml(item.badgeLabel)}</span>`
      : ""
    const label = item.label
      ? `<div class="text-[10px] text-gray-500 leading-snug mt-0.5">${this.escapeHtml(item.label)}</div>`
      : ""
    return `
      <div class="preview-row flex items-start gap-2 bg-white p-2 rounded border border-gray-200" data-preview-key="${this.escapeHtml(item.key)}"${this.sortableValue ? ' draggable="true"' : ""}>
        ${handle}
        <input type="hidden" name="selected_keys[]" value="${this.escapeHtml(item.key)}">
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-1 flex-wrap">
            <div class="font-mono font-bold text-gray-900 text-sm">${this.escapeHtml(item.key)}</div>
            ${typeBadge}
          </div>
          ${label}
        </div>
        <button type="button" data-action="click->question-select#removeFromPreview" class="text-gray-400 hover:text-red-600 shrink-0 cursor-pointer leading-none px-1" title="この設問を出力対象から外す">✕</button>
      </div>
    `
  }

  bindDragEvents() {
    this.previewTarget.querySelectorAll(".preview-row").forEach(row => {
      row.addEventListener("dragstart", this.onDragStart)
      row.addEventListener("dragover", this.onDragOver)
      row.addEventListener("drop", this.onDrop)
      row.addEventListener("dragend", this.onDragEnd)
    })
  }

  onDragStart = (event) => {
    this.draggedRow = event.currentTarget
    event.currentTarget.classList.add("opacity-50")
    event.dataTransfer.effectAllowed = "move"
  }

  onDragOver = (event) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
    const target = event.currentTarget
    if (target === this.draggedRow) return
    const rect = target.getBoundingClientRect()
    const midY = rect.top + rect.height / 2
    if (event.clientY < midY) {
      target.parentNode.insertBefore(this.draggedRow, target)
    } else {
      target.parentNode.insertBefore(this.draggedRow, target.nextSibling)
    }
  }

  onDrop = (event) => {
    event.preventDefault()
  }

  onDragEnd = () => {
    if (this.draggedRow) this.draggedRow.classList.remove("opacity-50")
    this.draggedRow = null
  }

  // 右カラムの「×」 ボタンから呼ばれる。 該当設問のチェックを外し、 プレビューを再描画する。
  removeFromPreview(event) {
    const row = event.currentTarget.closest(".preview-row")
    if (!row) return
    const key = row.dataset.previewKey
    const item = this.itemTargets.find(i => i.dataset.questionKey === key)
    if (item) {
      const checkbox = item.querySelector("input[type='checkbox']")
      if (checkbox) checkbox.checked = false
    }
    row.remove()
    if (this.currentPreviewKeys().length === 0) {
      this.previewTarget.innerHTML = `<div class="text-sm text-gray-400 py-12 text-center">設問を左から選択してください</div>`
    }
  }

  escapeHtml(str) {
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }
}
